import React, { useEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import {
    Modal,
    ModalBody,
    ModalCloseBtn,
    ModalContent,
    ModalHeader,
    ModalTrigger,
    useModalContext,
} from '@fiverr-private/overlay';
import { I18n } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { HandlePaymentRequest, LegalDocsConfigurations } from '../../../types';
import { BiData } from '../../../biEvents';
import { TemplateSelectionStep, BasicPlanDisclaimerStep } from './steps';
import { SendModalStepProps } from './steps/types';
import { SuccessStep } from './steps/SuccessStep';

export interface SendTemplateModalProps {
    children?: React.ReactNode;
    sellerId: number;
    sellerName: string;
    onSend?: () => void;
    configurations: LegalDocsConfigurations;
    isDisabled?: boolean;
    handlePayment: HandlePaymentRequest;
    isOpen?: boolean;
    onClose?: () => void;
    biData: BiData;
}

interface Step {
    component: React.FC<SendModalStepProps>;
    hideTitle?: boolean;
}

const PREMIUM_PACKAGE_STEPS: Step[] = [
    { component: TemplateSelectionStep },
    { component: SuccessStep, hideTitle: true },
];

const ESSENTIAL_PACKAGE_STEPS: Step[] = [
    { component: BasicPlanDisclaimerStep, hideTitle: true },
    ...PREMIUM_PACKAGE_STEPS,
];

const SKIPPED_MODAL_STEPS: Step[] = [...PREMIUM_PACKAGE_STEPS];

const TRANSLATION_PREFIX = 'legal_documents_package.send_modal';

export const SendTemplateModalContent = ({
    sellerId,
    sellerName,
    onSend = noop,
    configurations,
    handlePayment,
    biData,
}: Omit<SendTemplateModalProps, 'children'>) => {
    const { close, isOpen } = useModalContext();
    const { userId } = getContext();

    const [skipNextTime, setSkipNextTime] = useState(() => localStorage.getItem(`skipNextTime_${userId}`) === 'true');

    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0); // reset step when modal reopens
            setSkipNextTime(localStorage.getItem(`skipNextTime_${userId}`) === 'true');
        }
        // no need for `userId` at the dependency because it changes only when page reset
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const displayedSteps = useMemo(() => {
        if (configurations?.additionalData?.hideLegalDocumentsModal || skipNextTime) {
            return SKIPPED_MODAL_STEPS;
        }
        return configurations?.legalDocumentsPackageData?.isFree ? PREMIUM_PACKAGE_STEPS : ESSENTIAL_PACKAGE_STEPS;
    }, [
        configurations?.additionalData?.hideLegalDocumentsModal,
        configurations?.legalDocumentsPackageData?.isFree,
        skipNextTime,
    ]);

    const [currentStep, setCurrentStep] = useState(0);
    const handleModalClose = () => {
        close();
        setCurrentStep(0);
    };

    const onNextStep = () => {
        if (!displayedSteps[currentStep + 1]) {
            handleModalClose();
        }

        setCurrentStep(currentStep + 1);
    };

    const displayedStep = displayedSteps[currentStep];
    const StepComponent = displayedStep.component;

    return (
        <ModalContent onAnimationEnd={() => setCurrentStep(0)}>
            <ModalHeader>
                {displayedStep.hideTitle ? '' : <I18n k={`${TRANSLATION_PREFIX}.selection_step.title`} />}
                <ModalCloseBtn />
            </ModalHeader>
            <ModalBody>
                <StepComponent
                    onSubmit={onNextStep}
                    sellerId={sellerId}
                    sellerName={sellerName}
                    onSend={onSend}
                    configurations={configurations}
                    handlePayment={handlePayment}
                    biData={biData}
                />
            </ModalBody>
        </ModalContent>
    );
};

export const SendTemplateModal = (props: SendTemplateModalProps) => {
    const { children, isDisabled, isOpen, onClose, ...contentProps } = props;

    return (
        <Modal isOpen={isOpen} onClose={onClose} centered>
            <ModalTrigger isDisabled={isDisabled}>{children}</ModalTrigger>
            <SendTemplateModalContent isOpen={isOpen} {...contentProps} />
        </Modal>
    );
};
